import { useState } from "react";
import { MapPin, Mail, CheckCircle2, Send, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { useSite, FALLBACK_SETTINGS } from "@/lib/content";

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  project_type: "", budget: "", timeline: "", description: "", website: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-700/80 bg-slate-900/60 px-4 py-3 text-sm text-slate-100 placeholder-slate-500 transition-[border-color,box-shadow] duration-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30";

export default function Contact() {
  const { data } = useSite();
  const s = { ...FALLBACK_SETTINGS, ...(data?.settings || {}) };
  const seo = data?.settings?.seo?.["/contact"];

  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={seo?.title || "Start a Project | Contact LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "Tell LogiTech Nexus what you want to build. Start a custom software, web application or automation project with our team in Tacoma, WA."}
      />
      <PageHero
        eyebrow="Contact"
        title="Start a Project."
        subtitle="Tell us what you're trying to build or solve. The more you share about your operation, the more useful our first conversation will be."
        testid="contact-hero"
      />

      <section className="bg-void pb-28" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1fr_1.4fr] lg:px-10">
          <Reveal>
            <div className="glass rounded-2xl p-8" data-testid="contact-info-card">
              <h2 className="font-display text-lg font-semibold text-white">LogiTech Nexus</h2>
              <address className="mt-6 space-y-5 text-sm not-italic text-slate-300">
                <p className="flex gap-3">
                  <MapPin size={18} className="mt-0.5 shrink-0 text-blue-400" />
                  <span>
                    {s.addressLine1}
                    <br />
                    {s.addressLine2}
                    <br />
                    {s.addressCountry}
                  </span>
                </p>
                <p>
                  <a href={`mailto:${s.email}`} data-testid="contact-email-link" className="flex items-center gap-3 transition-colors hover:text-white">
                    <Mail size={18} className="shrink-0 text-blue-400" />
                    {s.email}
                  </a>
                </p>
              </address>
              <div className="mt-8 border-t border-slate-800 pt-6">
                <p className="text-sm leading-relaxed text-slate-400">
                  Based in Tacoma, Washington — working with businesses locally and
                  beyond. Every inquiry is read by a real person, and we respond
                  with clear next steps.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            {submitted ? (
              <div className="flex h-full flex-col items-start justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-10" data-testid="contact-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400">
                  <CheckCircle2 size={28} />
                </span>
                <h2 className="mt-6 font-display text-2xl font-semibold text-white">Thank you.</h2>
                <p className="mt-3 max-w-md text-base leading-relaxed text-slate-300">
                  Thank you for contacting LogiTech Nexus. We've received your
                  project information and will be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="rounded-2xl border border-slate-800/80 bg-navy/60 p-8" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Name *</label>
                    <input id="cf-name" data-testid="contact-name-input" required maxLength={100} className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="cf-company" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Company</label>
                    <input id="cf-company" data-testid="contact-company-input" maxLength={120} className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Business Email *</label>
                    <input id="cf-email" data-testid="contact-email-input" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Phone (optional)</label>
                    <input id="cf-phone" data-testid="contact-phone-input" type="tel" maxLength={40} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label htmlFor="cf-type" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Project Type *</label>
                    <select id="cf-type" data-testid="contact-type-select" required className={inputCls} value={form.project_type} onChange={set("project_type")}>
                      <option value="" disabled>Select a project type</option>
                      {(s.projectTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cf-budget" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Estimated Budget</label>
                    <select id="cf-budget" data-testid="contact-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                      <option value="">Prefer not to say</option>
                      {(s.budgetOptions || []).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-timeline" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Desired Timeline</label>
                    <select id="cf-timeline" data-testid="contact-timeline-select" className={inputCls} value={form.timeline} onChange={set("timeline")}>
                      <option value="">Not sure yet</option>
                      {(s.timelineOptions || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-description" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Project Description *</label>
                    <textarea id="cf-description" data-testid="contact-description-input" required minLength={10} maxLength={5000} rows={6} className={inputCls} value={form.description} onChange={set("description")} placeholder="What are you trying to build or improve? How does the process work today?" />
                  </div>
                </div>

                <div className="hidden" aria-hidden="true">
                  <label htmlFor="cf-website">Website</label>
                  <input id="cf-website" data-testid="contact-honeypot" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                </div>

                {error && (
                  <p className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="contact-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(59,130,246,0.4)] transition-[background-color,transform,opacity] duration-300 hover:bg-blue-500 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Project Inquiry
                      <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-4 text-xs text-slate-500">
                  Your information is used only to respond to your inquiry. See our Privacy Policy.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
